import React, { useState, useEffect, useRef } from 'react';
import { ArrowLeft, Loader2, RefreshCw, AlertTriangle, BookOpen, Maximize2, Minimize2 } from 'lucide-react';

export default function ApiDocs({ onBack, setError }) {
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [isFullscreen, setIsFullscreen] = useState(false);
  const containerRef = useRef(null);

  const ENV = process.env.REACT_APP_ENV;
  const API_BASE_URL = ENV === 'dev'
    ? process.env.REACT_APP_API_BASE_URL
    : window.location.origin;

  const documentationUrl = `${API_BASE_URL}/api-docs/documentation`;

  const handleIframeLoad = () => {
    setIsLoading(false);
    setLoadError('');
  };

  const handleIframeError = () => {
    setIsLoading(false);
    setLoadError('Failed to load documentation. Please check if the API server is running.');
    setError('Could not load API documentation. Please ensure the API server is accessible.');
  };

  const reloadDocs = () => {
    setIsLoading(true);
    setLoadError('');
    const iframe = document.getElementById('docs-iframe');
    if (iframe) {
      iframe.src = iframe.src;
    }
  };

  const toggleFullscreen = () => {
    if (!isFullscreen) {
      if (containerRef.current?.requestFullscreen) {
        containerRef.current.requestFullscreen();
      } else if (containerRef.current?.webkitRequestFullscreen) {
        containerRef.current.webkitRequestFullscreen();
      }
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen();
      }
    }
  };

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement || !!document.webkitFullscreenElement);
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
    };
  }, []);

  return (
    <div className="p-4 sm:p-6 lg:p-8 h-full flex flex-col">
      <div className="flex items-center justify-between mb-6">
        <button
          onClick={onBack}
          className="flex items-center space-x-2 text-white/80 hover:text-white transition-colors group"
        >
          <ArrowLeft className="w-5 h-5 group-hover:scale-110 transition-transform" />
          <span className="font-semibold">Back to Dashboard</span>
        </button>
        <h1 className="text-2xl sm:text-3xl font-bold text-white">API Documentation</h1>
        <button
          onClick={reloadDocs}
          disabled={isLoading}
          className="flex items-center space-x-2 px-4 py-2 bg-white/10 rounded-lg text-white/80 hover:bg-white/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
          <span className="hidden sm:inline">{isLoading ? 'Loading...' : 'Reload'}</span>
        </button>
      </div>

      {/* Documentation Content */}
      <div
        ref={containerRef}
        className="flex-grow bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl overflow-hidden relative flex flex-col"
        style={isFullscreen ? { background: '#0f172a' } : {}}
      >
        {/* Header */}
        <div className="p-4 border-b border-white/10 flex items-center justify-between bg-white/5 shrink-0">
          <div className="flex items-center space-x-3">
            <div className="p-2 rounded-lg bg-blue-500/10">
              <BookOpen className="w-5 h-5 text-blue-400" />
            </div>
            <div>
              <h3 className="text-white font-semibold">Notification Service API Portal</h3>
              <p className="text-white/60 text-sm">Comprehensive API documentation for all services</p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            {isLoading && (
              <div className="flex items-center space-x-2 text-white/60">
                <Loader2 className="w-4 h-4 animate-spin" />
                <span className="text-sm">Loading...</span>
              </div>
            )}
            {/* Fullscreen Toggle Button */}
            <button
              onClick={toggleFullscreen}
              title={isFullscreen ? 'Exit Fullscreen' : 'Enter Fullscreen'}
              className="flex items-center space-x-2 px-3 py-1.5 bg-white/10 hover:bg-white/20 text-white/80 hover:text-white rounded-lg transition-all duration-200 group"
            >
              {isFullscreen ? (
                <>
                  <Minimize2 className="w-4 h-4 group-hover:scale-110 transition-transform" />
                  <span className="text-sm hidden sm:inline">Exit Fullscreen</span>
                </>
              ) : (
                <>
                  <Maximize2 className="w-4 h-4 group-hover:scale-110 transition-transform" />
                  <span className="text-sm hidden sm:inline">Fullscreen</span>
                </>
              )}
            </button>
          </div>
        </div>

        {/* Content Area */}
        <div className="relative flex-grow">
          {loadError ? (
            <div className="w-full h-full flex flex-col items-center justify-center text-red-400 p-8 text-center">
              <AlertTriangle className="w-16 h-16 mb-4" />
              <p className="text-xl font-bold mb-2">Failed to Load Documentation</p>
              <p className="text-white/60 mb-4">{loadError}</p>
              <button
                onClick={reloadDocs}
                className="px-4 py-2 bg-red-500/20 text-red-300 rounded-lg hover:bg-red-500/30 transition-colors"
              >
                Try Again
              </button>
            </div>
          ) : (
            <>
              {isLoading && (
                <div className="absolute inset-0 bg-black/20 backdrop-blur-sm z-10 flex items-center justify-center">
                  <div className="text-center">
                    <Loader2 className="w-12 h-12 animate-spin text-white mb-4 mx-auto" />
                    <p className="text-white font-medium">Loading Documentation...</p>
                    <p className="text-white/60 text-sm mt-2">Please wait while we fetch the API docs</p>
                  </div>
                </div>
              )}
              <iframe
                id="docs-iframe"
                src={documentationUrl}
                title="API Documentation Portal"
                className="w-full border-0"
                style={{ height: isFullscreen ? 'calc(100vh - 72px)' : 'calc(100vh - 280px)' }}
                onLoad={handleIframeLoad}
                onError={handleIframeError}
                sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
                referrerPolicy="same-origin"
              />
            </>
          )}
        </div>
      </div>

      {/* Footer Info */}
      {!isFullscreen && (
        <div className="mt-4 text-center">
          <p className="text-white/40 text-sm">
            Documentation served from: <code className="text-white/60">{API_BASE_URL}</code>
          </p>
        </div>
      )}
    </div>
  );
}